import React, { useEffect } from 'react';
import {
  MarketRow,
  MarketAccordionItem,
  MarketList,
  MarketButton,
  MarketAccessory,
} from '@market/react';
import { logoutUrl } from 'utils/environment';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { closeModal } from 'store/modalSlice';
import { useTranslation } from 'react-i18next';
import { AppState } from 'store';
import { selectBoolFlag, selectStringFlag } from 'store/featureSlice';
import { AuthenticatedStringFlagKey, BoolFlag } from '../models/Flags';
import { AnonymousBoolFlag } from 'routes/profile/models/Flags';
import useIsBlockEmployee from '../hooks/useIsBlockEmployee';
import GiftCardIcon from 'svgs/referrals/GiftCardIcon';
import { buyerportalCdpClient } from 'services/tracking/cdp/clients/buyerportal';
import { clickReferButtonProperties } from 'services/tracking/cdp/events/referrals';

type SideNavProps = {
  showSignOut: boolean;
};

type NavItem = {
  name: string;
  url?: string;
  subitems?: NavItem[];
  // Match the pathname exactly instead of by substring. Needed when one nav
  // url is a prefix of another (e.g. /purchasehistory vs
  // /purchasehistory/insights) so they don't both highlight at once.
  exactMatch?: boolean;
};

const isNavItemSelected = (item: NavItem): boolean => {
  if (!item.url) {
    return false;
  }
  return item.exactMatch
    ? window.location.pathname === item.url
    : window.location.href.includes(item.url);
};

const SideNav: React.FC<SideNavProps> = ({ showSignOut }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();

  const marketMonochrome = useSelector((state: AppState) =>
    selectBoolFlag(state, AnonymousBoolFlag.marketMonochrome)
  );

  const referralsExperimentVariant = useSelector((state: AppState) =>
    selectStringFlag(
      state,
      AuthenticatedStringFlagKey.referralsExperimentVariant
    )
  );
  const showRefer = referralsExperimentVariant === 'treatment';

  useEffect(() => {
    if (referralsExperimentVariant) {
      buyerportalCdpClient.track('Experiment Activation', {
        experimentVariant: referralsExperimentVariant,
        feature: 'Referrals',
      });
    }
  }, [referralsExperimentVariant]);

  const subItemClassName = (isSelected: boolean) => {
    if (marketMonochrome) {
      return `pl-4 hover:transition-all hover:bg-coreFill50 active:bg-coreFill50 ${
        isSelected ? 'bg-emphasis30 hover:bg-emphasis30' : ''
      } rounded-lg cursor-pointer`;
    } else {
      return `pl-4 hover:transition-all hover:bg-marketBlueHover active:bg-marketBlueActive ${
        isSelected ? 'bg-marketBlueHover text-emphasisBlue' : ''
      } rounded-lg cursor-pointer`;
    }
  };

  const isBlockEmployee = useIsBlockEmployee();
  const showReceipts =
    useSelector((state: AppState) =>
      selectBoolFlag(state, BoolFlag.showReceipts)
    ) || isBlockEmployee;
  const showPurchaseInsights =
    useSelector((state: AppState) =>
      selectBoolFlag(state, BoolFlag.showPurchaseInsights)
    ) || isBlockEmployee;

  const renderNavItem = (item: NavItem) => {
    if (!showSignOut && item.url === logoutUrl()) {
      return null;
    }

    if (item.subitems) {
      const isSubItemSelected = item.subitems.some(isNavItemSelected);

      return (
        <MarketAccordionItem
          key={`sub-nav:${item.name}`}
          className={'cursor-pointer'}
          name={item.name}
          size="medium"
          expanded={isSubItemSelected}
          aria-expanded={isSubItemSelected}
          role="button"
          style={{
            boxShadow: 'none',
          }}
        >
          <span slot="label" className="font-medium text-base tracking-wider">
            {item.name}
          </span>
          {item.subitems.map((item) => {
            const isSelected = isNavItemSelected(item);
            return (
              <div className="ml-4" key={`child-sub-nav:${item.name}`}>
                <div className={subItemClassName(isSelected)}>
                  {renderNavItem(item)}
                </div>
              </div>
            );
          })}
        </MarketAccordionItem>
      );
    }

    const isSelected = isNavItemSelected(item);

    return (
      <MarketRow
        className={`cursor-pointer ${
          isSelected && marketMonochrome ? 'bg-emphasis30' : ''
        }`}
        interactive
        key={`nav:${item.name}`}
        onClick={() => {
          if (item.url === logoutUrl()) {
            window.location.replace(logoutUrl());
          } else {
            history.push(item.url || '');
            dispatch(closeModal());
          }
        }}
        value={item.name}
        selected={isSelected || undefined}
      >
        <span
          className={`font-medium text-black text-md ${
            isSelected && !marketMonochrome ? 'text-emphasisBlue' : ''
          }`}
        >
          {item.name}
        </span>
        <div className="shadow-bottomDivider w-full h-1 absolute bottom-0"></div>
      </MarketRow>
    );
  };

  const items: NavItem[] = [
    {
      name: t('sidenav.paymentMethods'),
      url: '/paymentmethods',
    },
    {
      name: t('sidenav.profile'),
      url: '/account',
    },
    {
      name: t('sidenav.preferencesAndSettings'),
      subitems: [
        {
          name: t('sidenav.receiptSettings'),
          url: '/receiptsettings',
        },
        {
          name: t('sidenav.marketingPreferences'),
          url: '/marketingpreferences',
        },
      ],
    },
    {
      name: t('sidenav.signOut'),
      url: logoutUrl(),
    },
  ];

  const purchaseSubitems: NavItem[] = [];
  if (showReceipts) {
    purchaseSubitems.push({
      exactMatch: true,
      name: 'History',
      url: '/purchasehistory',
    });
  }
  if (showPurchaseInsights) {
    purchaseSubitems.push({
      exactMatch: true,
      name: 'Insights',
      url: '/purchasehistory/insights',
    });
  }
  if (purchaseSubitems.length > 0) {
    items.splice(0, 0, {
      name: 'Purchases',
      subitems: purchaseSubitems,
    });
  }

  return (
    <nav className="px-2">
      {showRefer && (
        <MarketButton
          onClick={() => {
            buyerportalCdpClient.track(
              'Click Refer Button',
              clickReferButtonProperties()
            );
            history.push('/refer');
            dispatch(closeModal());
          }}
          className="mb-4"
        >
          <MarketAccessory slot="icon">
            <GiftCardIcon />
          </MarketAccessory>
          <span className="whitespace-nowrap">{t('sidenav.refer')}</span>
        </MarketButton>
      )}
      <MarketList>{items.map((item) => renderNavItem(item))}</MarketList>
    </nav>
  );
};

export default SideNav;
